import { checkUser } from '@/mixins/LoginMixin'
import { defineStore } from 'pinia'
import Logging from '@serv/Logging'
import Request from '@serv/Request'
import Storage from '@serv/Storage'
import { useRouter } from 'vue-router'
import { UserTypeOptional } from '@/models/UserType'
import { useStore } from 'vuex'
import Whoami from '@serv/Whoami'
import { toRefs, useLocalStorage, type UseStorageOptions } from '@vueuse/core'

interface ImpersonationState {
    impersonatedUser: UserTypeOptional | null
    originalUser: UserTypeOptional | null
    justFinishedImpersonation: boolean
    isInitialReloadPending: boolean
}

const defaultImpersonationState: ImpersonationState = {
    impersonatedUser: null,
    originalUser: null,
    justFinishedImpersonation: false,
    isInitialReloadPending: false
}

const localStorageOptions: UseStorageOptions<ImpersonationState> = {
    serializer: {
        read: (value: string) => {
            return value ? JSON.parse(value) : defaultImpersonationState
        },
        write: (value: ImpersonationState) => {
            return JSON.stringify(value)
        }
    },
    writeDefaults: false
}

export const useImpersonationStore = defineStore('impersonation', () => {
    const impersonationState = useLocalStorage('impersonation', defaultImpersonationState, localStorageOptions)
    const { impersonatedUser, originalUser, justFinishedImpersonation, isInitialReloadPending } =
        toRefs(impersonationState)

    const store = useStore()
    const router = useRouter()

    async function navigateToUserHomePage() {
        try {
            await Whoami.getActiveUser(impersonatedUser.value)
        } catch (error) {
            throw new Error(`Failed to refresh user data: ${error}`)
        }

        try {
            const navigation = await Whoami.getNavigation()
            window.location.assign(router.resolve({ name: navigation.HomePage }).href)
        } catch (error) {
            Logging.error(`Failed to navigate to user home page: ${error}`)
            window.location.assign('/')
        }
    }

    async function impersonateUser(user: UserTypeOptional) {
        originalUser.value = store.getters.user
        impersonatedUser.value = user
        Storage.set('spoof', true)
        isInitialReloadPending.value = true
        Request.setImpersonationUser(user)
        await checkUser({ impersonatedUser: user })
    }

    async function stopImpersonating({ skipRefreshingUser = false }: { skipRefreshingUser?: boolean } = {}) {
        if (!impersonatedUser.value) {
            return
        }

        impersonatedUser.value = null
        originalUser.value = null
        Storage.set('spoof', false)
        Request.setImpersonationUser(undefined)

        if (!skipRefreshingUser) {
            await checkUser({ forceRefreshUser: true })
        }
        justFinishedImpersonation.value = true
    }

    async function $reset() {
        try {
            await stopImpersonating()
        } catch (error) {
            Logging.error(`Failed to stop impersonating: ${error}`)
            await stopImpersonating({ skipRefreshingUser: true })
        }
        Storage.remove('impersonation')
        impersonationState.value = defaultImpersonationState
    }

    return {
        impersonatedUser,
        originalUser,
        justFinishedImpersonation,
        impersonateUser,
        stopImpersonating,
        navigateToUserHomePage,
        isInitialReloadPending,
        $reset
    }
})
